import React, { useEffect, useState } from 'react'
import { Modal, DatePicker, Row, Col, Typography, Space, Alert } from 'antd'
import { Button } from '@sureco/design-system'
import dayjs from 'dayjs'
import { isDateInRange } from 'helpers/date-validation'
import { EnrollmentConfiguration } from '../hooks/useEnrollmentConfiguration'
import { RescheduleEnrollmentDatesDto } from '../hooks/useEnrollmentPeriodDates'

const { Title, Text } = Typography

export type EditEnrollmentDatesMode = 'all' | 'subset'

interface EditEnrollmentDatesModalProps {
  open: boolean
  mode: EditEnrollmentDatesMode
  enrollment: EnrollmentConfiguration
  /** Only meaningful (and shown) in "subset" mode — how many employees are
   * currently selected. "all" mode always applies to every employee tied to
   * this OEP, so there's nothing to count here. */
  affectedCount?: number
  isSubmitting?: boolean
  serverError?: string | null
  onClose: () => void
  onConfirm: (dto: Omit<RescheduleEnrollmentDatesDto, 'personIds'>) => void
}

const normalizeDate = (date: string | null) =>
  date ? dayjs(date).format('YYYY-MM-DD') : null

export default function EditEnrollmentDatesModal({
  open,
  mode,
  enrollment,
  affectedCount = 0,
  isSubmitting = false,
  serverError,
  onClose,
  onConfirm,
}: EditEnrollmentDatesModalProps) {
  // In "all" mode there's one current window to show and diff against — the
  // OEP's own dates. In "subset" mode the selected employees may each have
  // different existing dates, so the fields start blank.
  const originalStartDate =
    mode === 'all' ? enrollment.enrollmentStartDate : null
  const originalEndDate = mode === 'all' ? enrollment.enrollmentEndDate : null

  const [newEnrollmentStartDate, setNewEnrollmentStartDate] = useState<
    string | null
  >(originalStartDate)
  const [newEnrollmentEndDate, setNewEnrollmentEndDate] = useState<
    string | null
  >(originalEndDate)
  const [validationError, setValidationError] = useState<string | null>(null)

  const resetState = (): void => {
    setNewEnrollmentStartDate(originalStartDate)
    setNewEnrollmentEndDate(originalEndDate)
    setValidationError(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  // Re-prefill (or clear, in subset mode) whenever the modal opens, and
  // clear stale values once the parent closes it (e.g. after a successful
  // save) without necessarily going through handleClose.
  useEffect(() => {
    if (open) {
      setNewEnrollmentStartDate(originalStartDate)
      setNewEnrollmentEndDate(originalEndDate)
      setValidationError(null)
    } else {
      resetState()
    }
  }, [open, mode, originalStartDate, originalEndDate])

  const handleConfirm = () => {
    const startChanged =
      normalizeDate(newEnrollmentStartDate) !== normalizeDate(originalStartDate)
    const endChanged =
      normalizeDate(newEnrollmentEndDate) !== normalizeDate(originalEndDate)

    // The range/past-date guards below only apply to a field the admin is
    // actually changing — an untouched, pre-filled value (e.g. an OEP whose
    // start date is already outside the 1-year window, or whose end date is
    // already in the past) shouldn't block an edit to the *other* field.
    if (
      startChanged &&
      newEnrollmentStartDate &&
      !isDateInRange(newEnrollmentStartDate)
    ) {
      setValidationError('Start date must be within one year of today.')
      return
    }
    if (
      endChanged &&
      newEnrollmentEndDate &&
      !isDateInRange(newEnrollmentEndDate)
    ) {
      setValidationError('End date must be within one year of today.')
      return
    }
    if (
      newEnrollmentStartDate &&
      newEnrollmentEndDate &&
      !dayjs(newEnrollmentStartDate).isBefore(dayjs(newEnrollmentEndDate))
    ) {
      setValidationError('Start date must be before the end date.')
      return
    }
    if (
      endChanged &&
      newEnrollmentEndDate &&
      dayjs(newEnrollmentEndDate).isBefore(dayjs().startOf('day'))
    ) {
      setValidationError('End date cannot be in the past.')
      return
    }

    const dto = {
      applyToAllEnrolled: mode === 'all',
      ...(startChanged && newEnrollmentStartDate
        ? { newEnrollmentStartDate }
        : {}),
      ...(endChanged && newEnrollmentEndDate ? { newEnrollmentEndDate } : {}),
    }

    if (!dto.newEnrollmentStartDate && !dto.newEnrollmentEndDate) {
      setValidationError('Change at least one date before saving.')
      return
    }

    setValidationError(null)
    onConfirm(dto)
  }

  const pluralize = (count: number, singular: string, plural: string) =>
    `${count} ${count === 1 ? singular : plural}`

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={
        <Title level={4}>
          {mode === 'all'
            ? 'Edit Open Enrollment Period'
            : 'Edit Enrollment Dates'}
        </Title>
      }
      width={520}
    >
      <Text type="secondary">
        {mode === 'all' ? (
          <>
            Update the start and end dates for the open enrollment period of{' '}
            <strong>
              Enrollment {dayjs(enrollment.benefitEffectiveDate).format('YYYY')}
            </strong>
            .
          </>
        ) : (
          <>Update the start and end dates for the selected employee(s).</>
        )}
      </Text>

      <div
        style={{
          backgroundColor: '#fef2f2',
          borderLeft: '4px solid #f87171',
          padding: '12px 16px',
          borderRadius: 4,
          margin: '16px 0 24px',
        }}
      >
        <Text style={{ color: '#b91c1c' }}>
          {mode === 'all' ? (
            <>
              This will edit dates for <strong>every eligible employee</strong>{' '}
              in the company.
            </>
          ) : (
            <>
              This will update the enrollment window for{' '}
              <strong>
                {pluralize(
                  affectedCount,
                  'selected employee',
                  'selected employees'
                )}
              </strong>
              .
            </>
          )}
        </Text>
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Text strong>Start Date</Text>
          <DatePicker
            style={{ width: '100%', marginTop: 4 }}
            value={
              newEnrollmentStartDate ? dayjs(newEnrollmentStartDate) : null
            }
            onChange={(date) =>
              setNewEnrollmentStartDate(date ? date.format('YYYY-MM-DD') : null)
            }
          />
        </div>
        <div>
          <Text strong>End Date</Text>
          <DatePicker
            style={{ width: '100%', marginTop: 4 }}
            value={newEnrollmentEndDate ? dayjs(newEnrollmentEndDate) : null}
            onChange={(date) =>
              setNewEnrollmentEndDate(date ? date.format('YYYY-MM-DD') : null)
            }
          />
        </div>

        {(validationError || serverError) && (
          <Alert
            type="error"
            showIcon
            message={validationError ?? serverError}
          />
        )}

        <Row justify="end" gutter={8}>
          <Col>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </Col>
          <Col>
            <Button
              variant="primary"
              onClick={handleConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Dates'}
            </Button>
          </Col>
        </Row>
      </Space>
    </Modal>
  )
}
